import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getSessionId, getToken } from "@/utils/storage";
import { getStoredGuestExamUser } from "@/api/exam";
import { ProgressBar } from "@/components/ProgressBar";

const Task2ProcessingScreen = () => {
  const [currentMessageIndex, setCurrentMessageIndex] = useState(0);
  const [isProcessing, setIsProcessing] = useState(true);
  const [isComplete, setIsComplete] = useState(false);
  const navigate = useNavigate();

  // IDENTICAL format to CombinedFeedbackProcessing
  const statusMessages = [
    "Reviewing your Task 2 conversation...",
    "Measuring grammar, clarity, and accuracy...",
    "Evaluating your question structure...",
    "Preparing your personalized feedback...",
  ];

  // -----------------------------
  // AUDIO CLEANUP (unchanged)
  // -----------------------------
  useEffect(() => {
    (async () => {
      try {
        const globalAudioRef = (window as any).audioServiceRef?.current;
        if (globalAudioRef) {
          globalAudioRef.stopRecording?.();
          globalAudioRef.cancelAIAudio?.();
          globalAudioRef.cleanup?.();
        }
      } catch {}

      try {
        if (navigator?.mediaDevices?.getUserMedia) {
          const s = await navigator.mediaDevices.getUserMedia({ audio: true });
          try {
            s.getTracks().forEach((t) => {
              try {
                (MediaStreamTrack.prototype as any).stop.call(t);
              } catch {
                try {
                  t.stop();
                } catch {}
              }
            });
          } catch {}
        }
      } catch {}

      try {
        const ctx: AudioContext | undefined = (window as any).__appAudioContext;
        if (ctx?.close) {
          await ctx.close();
          (window as any).__appAudioContext = undefined;
        }
      } catch {}
    })();
  }, []);

  // -----------------------------
  // Rotate messages
  // -----------------------------
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentMessageIndex((prev) => (prev + 1) % statusMessages.length);
    }, 3000);

    return () => clearInterval(interval);
  }, [statusMessages.length]);

  // -----------------------------
  // Process Task 2 feedback (unchanged)
  // -----------------------------
  useEffect(() => {
    const processTask2 = async () => {
      try {
        const sessionIdFromStorage = getSessionId();
        const currentExamId = localStorage.getItem("current_exam_id");
        const sessionId = currentExamId || sessionIdFromStorage;

        const paidToken = getToken();
        const guestUser = getStoredGuestExamUser();
        const guestToken =
          guestUser?.token ?? localStorage.getItem("guestToken");

        const isGuest = !paidToken && !!guestToken;
        const token = paidToken || guestToken;

        if (!sessionId || !token) {
          setIsProcessing(false);
          setIsComplete(true);
          return;
        }

        console.log('🎯 TASK 2 RETRY PROCESSING - Starting background processing...');
        console.log('🎯 TASK 2 RETRY PROCESSING - Audio upload should already be complete');
        
        // Call grammar analysis for Task 2 retry
        try {
          console.log('🎯 TASK 2 RETRY PROCESSING - Calling grammar analysis...');
          
          // Build grammar analysis payload with guest support
          const grammarPayload: any = { 
            session_id: sessionId,
            task_type: 2,
            task2_topic_id: localStorage.getItem('task2_topic_id') || null,
            task_attempt_id: localStorage.getItem('current_task_attempt_id')
          };
          if (isGuest) {
            grammarPayload.guest_id = guestUser?.user_id;
            grammarPayload.is_guest = true;
          }
          
          console.log('🎯 TASK 2 RETRY PROCESSING - Grammar payload built:', {
            payload: grammarPayload,
            payloadSize: JSON.stringify(grammarPayload).length,
            isGuest,
            sessionId,
            task2TopicId: grammarPayload.task2_topic_id
          });
          
          console.log('🎯 TASK 2 RETRY PROCESSING - Making gramm-vocab-compre API call...');
          console.log('🎯 TASK 2 RETRY PROCESSING - API URL: https://nluntjftnyzlxhatvvks.supabase.co/functions/v1/task-1-2-individual');
          console.log('🎯 TASK 2 RETRY PROCESSING - Headers:', {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token ? token.substring(0, 20) + '...' : 'NO_TOKEN'}`
          });
          
          const grammarResponse = await fetch('https://nluntjftnyzlxhatvvks.supabase.co/functions/v1/task-1-2-individual', {
            method: 'POST',
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify(grammarPayload),
          });

          if (grammarResponse.ok) {
            const result = await grammarResponse.json();
            if (result.analysis_data) {
              localStorage.setItem(
                "latest_analysis_data",
                JSON.stringify(result.analysis_data)
              );
            }
          }
        } catch (innerError) {
          console.error("Task 2 grammar analysis error:", innerError);
        }

        await new Promise((res) => setTimeout(res, 1500));
        setIsProcessing(false);
        setIsComplete(true);
      } catch (error) {
        console.error("Task 2 retry error:", error);
        setIsProcessing(false);
        setIsComplete(true);
      }
    };

    processTask2();
  }, []);

  // -----------------------------
  // Navigate after progress completes
  // -----------------------------
  useEffect(() => {
    if (!isComplete) return;

    const timeout = setTimeout(() => {
      navigate("/single-task-feedback");
    }, 400);

    return () => clearTimeout(timeout);
  }, [isComplete, navigate]);

  // -----------------------------
  // FINAL UI — PERFECT 1:1 COPY OF CombinedFeedbackProcessing
  // -----------------------------
  return (
    <div
      className="
        flex flex-col items-center justify-center
        relative overflow-hidden
        h-full
        px-4 py-12
        md:px-6 md:py-16
      "
    >
      <div className="flex flex-col items-center text-center max-w-2xl mx-auto">

        {/* Spinner (exact size) */}
        <div className="relative mb-6">
          <div className="w-24 h-24 md:w-28 md:h-28 rounded-full bg-primary/10 flex items-center justify-center">
            <div className="w-10 h-10 md:w-12 md:h-12 border-4 border-primary border-t-transparent rounded-full animate-spin" />
          </div>
        </div>

        {/* Title (exact typography) */}
        <h1 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
          Preparing Your Task 2 Results
        </h1>

        {/* Status Message */}
        <div className="space-y-3 mb-6">
          <p className="text-lg md:text-xl text-muted-foreground">
            {statusMessages[currentMessageIndex]}
          </p>
        </div>

        {/* Progress Bar (identical) */}
        <ProgressBar isComplete={isComplete} />

        {/* Bottom text (identical) */}
        <div className="mt-8 text-sm text-muted-foreground max-w-xl mx-auto">
          <p>
            This usually takes less than a minute. Please keep this tab open so your
            results and next-step tips can load.
          </p>
        </div>
      </div>
    </div>
  );
};

export default Task2ProcessingScreen;
